import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import Loading from "../../Shared/Loading";
import SuperAdminAllPatientTable from "./SuperAdminAllPatientTable";
import SuperAdminAddUserModal from "./SuperAdminModal/SuperAdminAddUserModal";
import Pagination from "../../Shared/Pagination";
import { useQuery } from "../../Hooks/useApi";

const SuperAdminNonReferralUser = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [refetchState, setRefetchState] = useState(false);
  const [isOpenAddUser, setIsOpenAddUser] = useState(false);
  const limit = 10;

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const { data, loading, refetch } = useQuery(
    `/user/specific?fieldName=role&fieldValue=user&fieldName2=referralName&fieldValue2=null&page=${page}&limit=${limit}&search=${encodeURIComponent(debouncedSearch)}`
  );

  const user = data?.data || [];
  const filteredUsers = data?.data || [];
  const totalPages = data?.pages || 1;
  const totalResults = data?.total || 0;

  const setRefetch = () => {
    setRefetchState((prev) => !prev);
    refetch();
  };

  const handleSearch = () => {
    setPage(1);
    refetch();
  };

  // Current date formatting
  const date = new Date();
  const day = date.getDate();
  const year = date.getFullYear();
  const monthName = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ][date.getMonth()];
  const formattedDate = `${day} ${monthName} ${year}`;

  const openModalAddUser = () => {
    setIsOpenAddUser(true);
  };

  const closeModalAddUser = () => {
    setIsOpenAddUser(false);
  };

  return (
    <div className="space-y-6 select-none">
      {/* Header Widget */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
            <Icon icon="solar:users-group-two-rounded-bold" className="text-cyan-500 w-6 h-6" />
            Direct User Accounts
          </h1>
          <p className="text-[11px] text-slate-400 font-bold mt-1 uppercase tracking-wider">{formattedDate}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-2 text-indigo-700 font-bold text-xs">
            Direct Users: <span className="text-indigo-900 font-black ml-1">{totalResults}</span>
          </div>
          <button
            onClick={openModalAddUser}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-xs rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
          >
            <Icon icon="gala:add" className="w-4 h-4" />
            Add User
          </button>
        </div>
      </div>

      {/* Search Bar & Table Wrapper */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="p-5 border-b border-slate-50 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search user by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm placeholder-slate-400 transition-all"
            />
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <Icon icon="solar:magnifer-linear" className="w-5 h-5" />
            </div>
          </div>
          <button
            onClick={handleSearch}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-xs px-5 py-2.5 rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
          >
            Search
          </button>
        </div>

        {/* Table View */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-20 flex justify-center items-center">
              <Loading />
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100">
                  <th className="px-6 py-4 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-4 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Referral Doctor</th>
                  <th className="px-6 py-4 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((resellerData) => (
                    <SuperAdminAllPatientTable
                      key={resellerData?._id}
                      refetch={refetch}
                      setRefetch={setRefetch}
                      resellerData={resellerData}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-sm text-slate-400">
                      No direct direct user accounts found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex justify-end mt-4">
          <Pagination page={page} totalPages={totalPages} setPage={setPage} />
        </div>
      )}

      <SuperAdminAddUserModal
        isOpen={isOpenAddUser}
        closeModal={closeModalAddUser}
      />
    </div>
  );
};

export default SuperAdminNonReferralUser;
